import { memo, useRef } from "react"

import { Column } from "@/atoms"
import {
  getDate,
  getTimestampInMillisecondsFromSecond
} from "@/helpers/date.helpers"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { ColumnTooltip } from "@/pages/DashboardPage/components/molecules/Tooltip"
import { DISCOVERED_CLOSED_ISSUES_MAX } from "@/pages/DashboardPage/const/graph"

import type {
  ColumnRecord,
  DiscoveredClosedIssuesChartProps
} from "./DiscoveredClosedIssuesChart.types"

export const DiscoveredClosedIssuesChart = memo(
  function DiscoveredClosedIssuesChartComponent(
    props: DiscoveredClosedIssuesChartProps
  ) {
    const { data, legend = [] } = props
    const meta = Object.fromEntries(
      (legend || []).map((item) => [item.label.toLowerCase(), item])
    )
    const redirect = useRef(true)
    const { abbreviateNumber } = useNumberConvertor()
    const tickInterval = 100
    const min = 0 - DISCOVERED_CLOSED_ISSUES_MAX
    const max = DISCOVERED_CLOSED_ISSUES_MAX

    return (
      <div className="w-100-ks">
        <Column
          isStack
          autoFit
          height={235}
          xField="label"
          yField="value"
          seriesField="type"
          data={data}
          minColumnWidth={20}
          maxColumnWidth={20}
          columnStyle={(datum) => {
            if (datum.value >= max || datum.value <= min) return undefined

            const radius = 4
            const result = {
              radius: [radius, radius]
            }

            return result
          }}
          xAxis={{
            line: null
          }}
          yAxis={{
            min,
            max,
            tickInterval,
            grid: {
              line: {
                style: (_: unknown, index: number) => {
                  const noExcludedYAxisIndices = [1, 5, 15, 19]

                  return {
                    stroke: "#d9d9d9",
                    lineWidth: noExcludedYAxisIndices.includes(index) ? 0.5 : 0,
                    strokeOpacity: 1
                  }
                }
              }
            },
            label: {
              formatter: (text) => (`${text}` === "0" ? text : " ")
            }
          }}
          legend={false}
          color={({ type }) => meta[type].color}
          annotations={[
            {
              type: "line",
              start: ["start", 0],
              end: ["end", 0],
              style: {
                stroke: "#bfbfbf",
                lineWidth: 1
              }
            }
          ]}
          interactions={[
            {
              type: "element-highlight"
            },
            {
              type: "legend-filter",
              enable: false
            }
          ]}
          state={{
            active: {
              style: () => {
                return {
                  lineWidth: 2,
                  cursor: "pointer"
                }
              }
            },
            inactive: {
              style: {
                fillOpacity: 0.3
              }
            }
          }}
          tooltip={{
            customContent: (title, item) => {
              if (!item?.length) return null

              return (
                <ColumnTooltip
                  title={title}
                  items={[
                    { type: "discovered", field: "openedCount" },
                    { type: "resolved", field: "resolvedCount" }
                  ].map(({ type, field }) => ({
                    color: meta[type].color,
                    label: meta[type].label,
                    value: `${abbreviateNumber(item[0].data[field] || 0)}`
                  }))}
                />
              )
            }
          }}
          onReady={(plot) => {
            plot.on("click", ({ data }: { data: { data: ColumnRecord } }) => {
              const { type, startTimestamp, endTimestamp } = data?.data || {}

              if (!type) return

              const getLocalTimestamp = (ts: number) =>
                getDate(getTimestampInMillisecondsFromSecond(ts)).local().unix()

              if (redirect.current)
                queueMicrotask(() => {
                  props.redirectToTriage({
                    issueStatus: type === "discovered" ? "open" : type,
                    [`${type}-time`]: `${getLocalTimestamp(startTimestamp)},${getLocalTimestamp(endTimestamp)}`
                  })
                })

              redirect.current = false
            })
          }}
        />
      </div>
    )
  },
  (prev, next) => prev.data === next.data
)
